import { useResultBuffer } from '@/hooks/useResultBuffer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { OfflineWrapper } from '@/contexts/OfflineWrapper';
import { useAssetDownloader } from '@/contexts/DownloadAssetContext';
import utils from '@/utils/utils';
import ResponsiveText from '@/components/SharedComponents/ResponsiveText/ResponsiveText';
import { useResponsiveWidth } from '@/hooks/useResponsiveDimensions';
import { ImageExercise } from '@/components/ImageExercise';
import { SquareGrid } from '@/components/SquareGrid';
import { VideoPlayer } from '@/components/Video';
import Reading from './Reading';
import { Understanding } from './Understanding';
import { styles } from './styles';

const { shuffle } = utils;

const generateExerciseType = (setting, name) => {
  return {
    ...setting,
    name,
  };
};

const generateSetItemType = (key) => `${key}setitem`;

export function HearingExercise({
  settings,
  ablenkerFromSetJson,
  exercise: exerciseProp,
  currentExercise,
  isSolved,
  skip,
  isLesen,
  patientId,
  isSelf,
}) {
  const mistakes = useRef(0);
  const [images, setImages] = useState([]);
  const [correctSelected, setCorrectSelected] = useState(false);
  const currentSettings = settings?.[exerciseProp?.type];
  const videoUrl = exerciseProp?.video ?? '';
  const audioUrl = exerciseProp?.audio ?? '';
  const { setting, value, playbackRate } = currentSettings;
  const correctImage = exerciseProp.correct[0];
  const isSentence = exerciseProp.exerciseClass == 'sentence';
  const setItemType = generateSetItemType(exerciseProp.exerciseClass);
  const itemMargin = useResponsiveWidth(0.5);
  const { getLocalUri } = useAssetDownloader();

  const { resultCreator } = useResultBuffer({
    setItemType,
    setItemTypeId: exerciseProp.wordsetitem,
    exerciseType: generateExerciseType(currentSettings, exerciseProp?.type),
    patientId,
    shouldTracksMistakes: true,
    isSelf,
  });

  const onWrong = useCallback(() => {
    mistakes.current = mistakes.current + 1;
  }, []);

  const localUrlsGenerator = useCallback(async (wrongImgValue) => {
    for (let val of wrongImgValue) {
      val.url = await getLocalUri(val.url);
    }
    setImages(wrongImgValue);
    changeCorrectSelected(false);
    mistakes.current = 0;
  }, []);

  const changeCorrectSelected = useCallback((value) => {
    setCorrectSelected(value);
  }, []);

  const onCorrect = () => {
    if (isSelf) {
      changeCorrectSelected(true);
      resultCreator(mistakes.current);
    } else {
      changeCorrectSelected(true);
      isSolved();
      resultCreator(mistakes.current);
    }
  };

  const items = useMemo(() => {
    return images.map((image) => {
      return {
        props: {
          onCorrect,
          onWrong,
          isCorrect: image.isCorrect,
        },
        key: image.url,
        Element: ImageExercise,
      };
    });
  }, [images]);

  const generateElements = useCallback(
    (items, correctSelected) => {
      return items.map(({ Element, key, props }) => {
        return (
          <OfflineWrapper url={key} key={key + currentExercise}>
            <Element key={key} {...props} isCorrectSelected={correctSelected} />
          </OfflineWrapper>
        );
      });
    },
    [currentExercise],
  );

  const columns = 2;

  useEffect(() => {
    if (setting === 'fromset') {
      // parse Ablenker from set from json string
      const ablenkerFromSet = JSON.parse(ablenkerFromSetJson);
      // remove current setItem picture from Ablenker from set
      delete ablenkerFromSet[exerciseProp?.wordsetitem];
      exerciseProp.fromset = Object.keys(ablenkerFromSet).map(function (key) {
        return ablenkerFromSet[key];
      });
      shuffle(exerciseProp?.fromset);
    }
    // add Ablenker according to setting (related,unrelated, or fromset)
    const wrongImgValue = [];
    for (let i = 0; i < value - 1; i++) {
      // otherwise add related or unrelated Ablenker
      if (exerciseProp?.[setting]?.[i] != undefined) {
        wrongImgValue[i] = {
          isCorrect: false,
          url: exerciseProp?.[setting]?.[i],
        };
      } else {
        wrongImgValue[i] = {
          isCorrect: false,
          url: undefined,
        };
      }
    }

    const correctImageObj = {
      isCorrect: true,
      url: correctImage,
    };

    wrongImgValue.push(correctImageObj);
    shuffle(wrongImgValue);
    localUrlsGenerator(wrongImgValue);
    setImages(wrongImgValue);
    changeCorrectSelected(false);
    mistakes.current = 0;
  }, [currentExercise]);

  return (
    <View style={styles.flex1}>
      {isLesen ? (
        isSentence ? (
          <Reading.Sentence
            grid={
              <SquareGrid
                items={generateElements(items, correctSelected).map((x) => (
                  <View style={styles.flex1Square}>{x}</View>
                ))}
                columns={4}
                rows={1}
                itemMargin={itemMargin}
              />
            }
            action={
              isSelf ? (
                <View style={styles.fullRow}>
                  <View style={styles.width2thirds}>
                    <ResponsiveText style={styles.exerciseText}>
                      {exerciseProp?.word}
                    </ResponsiveText>
                  </View>
                  <View style={styles.width1thirdCenter}>
                    <View style={styles.maxH500}>
                      <OfflineWrapper url={isSentence ? audioUrl : videoUrl}>
                        <VideoPlayer
                          autoPlay={false}
                          noVideo={true}
                          correctSelected={correctSelected}
                          onCorrect={() => skip()}
                          playbackRate={playbackRate}
                        />
                      </OfflineWrapper>
                    </View>
                  </View>
                </View>
              ) : (
                <ResponsiveText style={styles.exerciseText}>
                  {exerciseProp?.word}
                </ResponsiveText>
              )
            }
          />
        ) : (
          <Reading.Word
            grid={
              <SquareGrid
                items={generateElements(items, correctSelected)}
                columns={columns}
                rows={2}
                itemMargin={itemMargin}
              />
            }
            action={
              isSelf ? (
                <View style={styles.flex1Center}>
                  <View style={styles.flex1End}>
                    <ResponsiveText style={styles.exerciseText}>
                      {exerciseProp?.word}
                    </ResponsiveText>
                  </View>
                  <View style={styles.flex1CenterFull}>
                    <View style={styles.maxH300}>
                      <OfflineWrapper url={isSentence ? audioUrl : videoUrl}>
                        <VideoPlayer
                          autoPlay={false}
                          onlyAudio={isSentence}
                          url={isSentence ? audioUrl : videoUrl}
                          correctSelected={correctSelected}
                          onCorrect={() => skip()}
                          playbackRate={playbackRate}
                        />
                      </OfflineWrapper>
                    </View>
                  </View>
                </View>
              ) : (
                <View style={styles.flex1Center}>
                  <View style={styles.flex1End}>
                    <ResponsiveText style={styles.exerciseText}>
                      {exerciseProp?.word}
                    </ResponsiveText>
                  </View>
                  <View style={styles.flex1End} />
                </View>
              )
            }
          />
        )
      ) : (
        <Understanding
          grid={
            <SquareGrid
              items={generateElements(items, correctSelected)}
              columns={columns}
              rows={2}
              itemMargin={itemMargin}
            />
          }
          videoPlayer={
            isSelf ? (
              <View style={styles.flex1JustifyEnd}>
                <View style={styles.maxH300Bottom}>
                  <OfflineWrapper url={isSentence ? audioUrl : videoUrl}>
                    <VideoPlayer
                      autoPlay
                      noVideo={isSentence}
                      url={isSentence ? audioUrl : videoUrl}
                      correctSelected={correctSelected}
                      onCorrect={() => skip()}
                      playbackRate={playbackRate}
                    />
                  </OfflineWrapper>
                </View>
              </View>
            ) : null
          }
        />
      )}
    </View>
  );
}
